import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal, discount } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-drawer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-wire bg-charcoal"
          >
            <div className="flex items-center justify-between border-b border-wire px-6 py-5">
              <p className="font-mono text-[11px] tracking-[0.35em] text-neutral-300">YOUR CART</p>
              <button data-testid="cart-drawer-close" onClick={() => setDrawerOpen(false)} className="p-1 text-neutral-400 hover:text-white" aria-label="Close cart">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-6" data-testid="cart-empty-state">
                  <p className="font-mono text-xs tracking-[0.25em] text-neutral-500">YOUR CART IS EMPTY</p>
                  <Link to="/shop" data-testid="cart-empty-shop-link" onClick={() => setDrawerOpen(false)}
                    className="border border-wire px-6 py-3 font-mono text-[11px] tracking-[0.3em] text-neutral-200 transition-colors hover:border-white">
                    SHOP THE DROP
                  </Link>
                </div>
              ) : (
                items.map((item) => (
                  <div key={`${item.slug}-${item.size}`} data-testid={`cart-item-${item.size}`} className="flex gap-4 border-b border-wire py-5">
                    <div className="h-24 w-20 shrink-0 overflow-hidden border border-wire bg-obsidian">
                      <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-display text-sm font-semibold uppercase tracking-wide text-neutral-100">{item.name}</p>
                          <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-500">SIZE {item.size}</p>
                        </div>
                        <button data-testid={`cart-remove-${item.size}`} onClick={() => removeItem(item.slug, item.size)}
                          className="text-neutral-600 transition-colors hover:text-white" aria-label="Remove item">
                          <X size={14} />
                        </button>
                      </div>
                      <div className="mt-auto flex items-center justify-between pt-3">
                        <div className="flex items-center border border-wire">
                          <button data-testid={`cart-qty-minus-${item.size}`} onClick={() => updateQty(item.slug, item.size, item.qty - 1)} className="px-2 py-1 text-neutral-400 hover:text-white" aria-label="Decrease quantity">
                            <Minus size={12} />
                          </button>
                          <span data-testid={`cart-qty-${item.size}`} className="px-3 font-mono text-xs text-neutral-200">{item.qty}</span>
                          <button data-testid={`cart-qty-plus-${item.size}`} onClick={() => updateQty(item.slug, item.size, item.qty + 1)} className="px-2 py-1 text-neutral-400 hover:text-white" aria-label="Increase quantity">
                            <Plus size={12} />
                          </button>
                        </div>
                        <p className="font-mono text-sm text-neutral-200">{money(item.price * item.qty)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-wire px-6 py-6">
                <div className="flex justify-between font-mono text-xs tracking-[0.2em] text-neutral-400">
                  <span>SUBTOTAL</span>
                  <span data-testid="cart-drawer-subtotal" className="text-neutral-100">{money(subtotal - discount)}</span>
                </div>
                <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-neutral-600">SHIPPING & TAX CALCULATED AT CHECKOUT</p>
                <button
                  data-testid="cart-drawer-checkout-button"
                  onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
                  className="mt-5 flex w-full items-center justify-center gap-3 bg-white py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300"
                >
                  CHECKOUT <ArrowRight size={14} />
                </button>
                <Link to="/cart" data-testid="cart-drawer-view-cart" onClick={() => setDrawerOpen(false)}
                  className="mt-3 block text-center font-mono text-[10px] tracking-[0.3em] text-neutral-500 underline-offset-4 hover:text-white hover:underline">
                  VIEW FULL CART
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
